import type { StatsRoom } from '../../types';

const numberFormatter = new Intl.NumberFormat('en-US');

export type StatsLayout = 'exec' | 'tv';

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatMaybe(value: number | null): string {
  return value == null ? '-' : formatNumber(value);
}

export function formatSigned(value: number, plus = true): string {
  if (value > 0) return `${plus ? '+' : ''}${formatNumber(value)}`;
  if (value < 0) return `-${formatNumber(Math.abs(value))}`;
  return '0';
}

export function formatTime(value: number | null): string {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

export function formatDateTime(value: number): string {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

export function sinceLabel(value: number): string {
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
  return new Intl.DateTimeFormat(undefined, sameDay
    ? { hour: 'numeric', minute: '2-digit' }
    : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date);
}

export function ago(value: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - value) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
}

export function toLocalInput(value: number | null): string {
  if (!value) return '';
  const date = new Date(value);
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function toneFor(value: number): string {
  return value > 0 ? 'positive' : value < 0 ? 'negative' : '';
}

export function roomDelta(room: StatsRoom): { text: string; tone: string } {
  if (room.count == null) return { text: '-', tone: '' };
  if (room.pending) return { text: 'Pre-show', tone: '' };
  if (room.settling) return { text: 'Settling', tone: '' };
  if (room.isNew) return { text: 'New', tone: '' };
  if (room.delta == null) return { text: '-', tone: '' };
  const meaningfulBaseline = Math.abs(room.baseline ?? 0) >= 10;
  const percent = room.percent == null || !meaningfulBaseline || Math.abs(room.percent) >= 1_000 ? '' : ` ${Math.abs(room.percent)}%`;
  return { text: `${formatSigned(room.delta)}${percent}`, tone: toneFor(room.delta) };
}

export function sortByAttendance(rooms: StatsRoom[]): StatsRoom[] {
  return [...rooms].sort((left, right) => (right.count ?? -1) - (left.count ?? -1) || left.id - right.id);
}

export function gridSpec(itemCount: number, layout: StatsLayout, forcedColumns: number | null): { columns: number; perPage: number } {
  if (forcedColumns && forcedColumns >= 1 && forcedColumns <= 6) return { columns: forcedColumns, perPage: forcedColumns * (layout === 'exec' ? 2 : 3) };
  if (layout === 'exec') {
    if (itemCount <= 4) return { columns: 2, perPage: 4 };
    if (itemCount <= 8) return { columns: 3, perPage: 6 };
    return { columns: 4, perPage: 8 };
  }
  if (itemCount <= 4) return { columns: 1, perPage: 4 };
  return { columns: 2, perPage: 6 };
}
